import { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import { parseItems, parseItemLine } from '../../utils/dealConstants'

const STATUS_LABELS = {
  quote_requested: 'Quote Requested',
  quote_sent: 'Quote Sent',
  awaiting_shipping_label: 'Awaiting Collection',
  shipping_arranged: 'Shipping Arranged',
  received: 'Received',
  accepted: 'Accepted'
}

export default function PartExDeals({ status = 'quote_requested' }) {
  const [deals, setDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingPrices, setEditingPrices] = useState({})
  const [savingFor, setSavingFor] = useState(null)
  const [sendingQuote, setSendingQuote] = useState(null)

  useEffect(() => { loadDeals() }, [status])

  async function loadDeals() {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/deals/part-ex?status=${status}`)
      setDeals(response.deals || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function savePxPrice(dealId, price) {
    setSavingFor(dealId)
    try {
      await api.patch(`/api/deals/${dealId}/px-price`, { px_offered_price: parseFloat(price) })
      setDeals(prev => prev.map(d => {
        if (d.id !== dealId) return d
        return { ...d, pxData: { ...d.pxData, px_offered_price: parseFloat(price) } }
      }))
      setEditingPrices(prev => { const next = { ...prev }; delete next[dealId]; return next })
    } catch (err) {
      alert(`Failed to save: ${err.message}`)
    } finally {
      setSavingFor(null)
    }
  }

  async function sendQuote(dealId, testEmail = null) {
    if (!confirm(testEmail ? `Send test PX quote to ${testEmail}?` : 'Send PX quote to customer?')) return
    setSendingQuote(dealId)
    try {
      const body = testEmail ? { test_email: testEmail } : {}
      const result = await api.post(`/api/deals/${dealId}/send-px-quote`, body)
      alert(`Quote sent to ${result.sent_to}`)
      loadDeals()
    } catch (err) {
      alert(`Failed to send: ${err.message}`)
    } finally {
      setSendingQuote(null)
    }
  }

  function getTradeInTotal(parsedItemsText) {
    const lines = parseItems(parsedItemsText)
    let total = 0
    for (const line of lines) {
      const parsed = parseItemLine(line)
      if (parsed && parsed.bp) total += parsed.bp
    }
    return total
  }

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <div className="loading-spinner" style={{ margin: '0 auto 12px' }} />
        <div style={{ color: 'var(--muted)' }}>Loading {STATUS_LABELS[status] || 'Part Ex'} deals...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div className="form-error">{error}</div>
        <button className="btn btn-primary" onClick={loadDeals} style={{ marginTop: 12 }}>Retry</button>
      </div>
    )
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '12px 20px', minWidth: 120 }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 600 }}>Deals</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{deals.length}</div>
        </div>
        <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, padding: '12px 20px', minWidth: 120 }}>
          <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', fontWeight: 600 }}>With PX Data</div>
          <div style={{ fontSize: 24, fontWeight: 700 }}>{deals.filter(d => d.pxData).length}</div>
        </div>
      </div>

      {deals.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
          No {STATUS_LABELS[status] || 'Part Ex'} deals
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {deals.map(deal => {
            const items = parseItems(deal.parsed_items)
            const parsedItems = items.map(parseItemLine).filter(Boolean)
            const tradeInTotal = getTradeInTotal(deal.parsed_items)
            const px = deal.pxData
            const pxQty = px ? (px.px_qty || 1) : 1
            const unitPrice = px ? (px.px_product_price || 0) : 0
            const stockTotal = unitPrice * pxQty

            const isEditing = editingPrices[deal.id] !== undefined
            const currentOffered = isEditing ? editingPrices[deal.id] : (px?.px_offered_price != null ? px.px_offered_price.toString() : '')
            const offeredPerUnit = parseFloat(currentOffered) || 0
            const offeredTotal = offeredPerUnit * pxQty
            const discount = stockTotal - offeredTotal
            const customerPays = offeredTotal - tradeInTotal

            return (
              <div key={deal.id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', background: 'white' }}>
                {/* Header */}
                <div style={{ padding: '14px 20px', background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{deal.dealname || 'Untitled Deal'}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
                      {deal.customer_name || ''} {deal.seller_email ? `(${deal.seller_email})` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'right' }}>
                    <div>Deal ID: {deal.id}</div>
                    <div>{deal.createdate ? new Date(deal.createdate).toLocaleDateString('en-GB') : ''}</div>
                  </div>
                </div>

                {/* Two columns: Trading In | Wants to Buy */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 120 }}>
                  {/* Left: Trading In */}
                  <div style={{ padding: 16, borderRight: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#dc2626', letterSpacing: 0.5 }}>Trading In</span>
                      <span style={{ fontSize: 11, color: 'var(--muted)' }}>Deal: {deal.id}</span>
                    </div>
                    {parsedItems.length === 0 ? (
                      <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>No items parsed</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {parsedItems.map((item, i) => (
                          <div key={i} style={{ fontSize: 13 }}>
                            <div style={{ fontWeight: 600 }}>{item.name}</div>
                            {item.details && <div style={{ color: 'var(--muted)', fontSize: 12 }}>{item.details}</div>}
                            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                              {item.condition && <span style={{ background: '#f3f4f6', padding: '1px 6px', borderRadius: 4, marginRight: 6 }}>{item.condition}</span>}
                              Qty: {item.qty || 1}
                              {item.sp != null && <span style={{ marginLeft: 8 }}>SP: £{item.sp.toFixed(2)}</span>}
                              {item.bp != null && <span style={{ marginLeft: 8, fontWeight: 600, color: '#059669' }}>BP: £{item.bp.toFixed(2)}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--border)', fontWeight: 700, fontSize: 14, color: '#059669' }}>
                      Total Trade-In: £{tradeInTotal.toFixed(2)}
                    </div>
                  </div>

                  {/* Right: Wants to Buy */}
                  <div style={{ padding: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#2563eb', marginBottom: 10, letterSpacing: 0.5 }}>Wants to Buy</div>
                    {!px ? (
                      <div style={{ fontSize: 13, color: 'var(--muted)', fontStyle: 'italic' }}>No stock item linked</div>
                    ) : (
                      <div>
                        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          {px.px_product_image && (
                            <img
                              src={px.px_product_image}
                              alt=""
                              style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--border)', flexShrink: 0 }}
                            />
                          )}
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{px.px_product_title}</div>
                            {px.px_product_sku && <div style={{ fontSize: 12, color: 'var(--muted)' }}>SKU: {px.px_product_sku}</div>}
                            {px.px_shopify_product_id && <div style={{ fontSize: 11, color: 'var(--muted)' }}>Shopify ID: {px.px_shopify_product_id}</div>}
                            <div style={{ fontSize: 13, marginTop: 4 }}>
                              <span>£{unitPrice.toFixed(2)} ea</span>
                              {pxQty > 1 && <span style={{ marginLeft: 6 }}>× {pxQty}</span>}
                              {px.px_product_cost != null && <span style={{ marginLeft: 8, color: 'var(--muted)' }}>(Cost: £{px.px_product_cost.toFixed(2)} ea)</span>}
                            </div>
                          </div>
                        </div>
                        <div style={{ marginTop: 12, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 14, fontWeight: 700, color: '#2563eb' }}>
                          Stock Total: £{stockTotal.toFixed(2)}
                          {px.px_product_cost != null && <span style={{ marginLeft: 12, color: '#dc2626', fontWeight: 600, fontSize: 12 }}>(Total Cost: £{(px.px_product_cost * pxQty).toFixed(2)})</span>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Offer Calculator — only on quote_requested tab */}
                {px && status === 'quote_requested' && (
                  <div style={{ padding: '14px 20px', background: '#fefce8', borderTop: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <label style={{ fontSize: 13, fontWeight: 600 }}>PX price per unit:</label>
                        <div style={{ position: 'relative' }}>
                          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: 'var(--muted)' }}>£</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max={unitPrice}
                            className="form-input"
                            value={currentOffered}
                            onChange={e => setEditingPrices(prev => ({ ...prev, [deal.id]: e.target.value }))}
                            style={{ width: 120, paddingLeft: 24, textAlign: 'right' }}
                            placeholder="0.00"
                          />
                        </div>
                        {pxQty > 1 && <span style={{ fontSize: 13, color: 'var(--muted)' }}>× {pxQty}</span>}
                        <button
                          className="btn btn-primary"
                          onClick={() => savePxPrice(deal.id, currentOffered)}
                          disabled={savingFor === deal.id || !currentOffered || parseFloat(currentOffered) < 0}
                          style={{ padding: '6px 16px', fontSize: 13 }}
                        >
                          {savingFor === deal.id ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>

                    {currentOffered && offeredPerUnit > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 16px', fontSize: 13, marginTop: 10, maxWidth: 350 }}>
                        <span style={{ color: 'var(--muted)' }}>PX Total ({pxQty > 1 ? `£${offeredPerUnit.toFixed(2)} × ${pxQty}` : 'per unit'}):</span>
                        <strong>£{offeredTotal.toFixed(2)}</strong>
                        <span style={{ color: 'var(--muted)' }}>Discount off retail:</span>
                        <strong style={{ color: '#dc2626' }}>£{discount.toFixed(2)}</strong>
                        <span style={{ color: 'var(--muted)' }}>Trade-in credit (BP):</span>
                        <strong style={{ color: '#059669' }}>-£{tradeInTotal.toFixed(2)}</strong>
                        <span style={{ color: 'var(--muted)', fontWeight: 600 }}>Customer Pays:</span>
                        <strong style={{ color: customerPays > 0 ? '#059669' : customerPays < 0 ? '#dc2626' : 'inherit', fontSize: 15 }}>
                          {customerPays < 0 ? `-£${Math.abs(customerPays).toFixed(2)}` : `£${customerPays.toFixed(2)}`}
                        </strong>
                        {discount > tradeInTotal && (
                          <>
                            <span></span>
                            <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 12 }}>Warning: Discount exceeds trade-in value</span>
                          </>
                        )}
                      </div>
                    )}

                    {px.px_offered_price != null && (
                      <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-primary"
                          onClick={() => sendQuote(deal.id)}
                          disabled={sendingQuote === deal.id}
                          style={{ padding: '8px 20px', fontSize: 13 }}
                        >
                          {sendingQuote === deal.id ? 'Sending...' : 'Send Quote to Customer'}
                        </button>
                        <button
                          className="btn"
                          onClick={() => sendQuote(deal.id, 'sbdownes@gmail.com')}
                          disabled={sendingQuote === deal.id}
                          style={{ padding: '8px 16px', fontSize: 12, background: '#f3f4f6', border: '1px solid #d1d5db' }}
                        >
                          Test → sbdownes@gmail.com
                        </button>
                        {!isEditing && <span style={{ fontSize: 12, color: 'var(--muted)' }}>Saved offer: £{px.px_offered_price.toFixed(2)} per unit</span>}
                      </div>
                    )}
                  </div>
                )}

                {/* Status info — for non quote_requested tabs */}
                {status !== 'quote_requested' && (
                  <div style={{ padding: '14px 20px', background: '#f0f9ff', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    {px?.px_offered_price != null && (
                      <span style={{ fontSize: 13, color: 'var(--muted)' }}>
                        PX Price: £{px.px_offered_price.toFixed(2)} per unit
                      </span>
                    )}
                    {px?.px_quote_sent_at && (
                      <span style={{ fontSize: 13, color: '#059669', fontWeight: 600 }}>
                        Quote sent {new Date(px.px_quote_sent_at).toLocaleDateString('en-GB')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
